// "From existing guides" place source for Build My Trip (2026-10-12).
// Instead of searching Google/AI for a destination's places, take them from
// one or more guides the user picked for that destination. Everything after
// the hand-over (which places fit the days, geographic ordering, opening-
// hours-aware scheduling, leftovers staying unscheduled) is the wizard's
// existing pipeline — it keeps the incoming order as the importance order
// (see buildScheduleFromAddedPlaces), so all this module has to do is put a
// guide's places in a sensible order and make them safe to drop into a
// different guide.
import Guide from "../Models/Guide";
import Places from "../Models/Places";
import { Place_Source } from "../Models/Place";
import Common from "./Common";

// Places that came from a guide this session, by object (adding a place to a
// guide keeps the same object and only changes its ID). Lets later steps
// that run elsewhere — mobile's enrichment lives in PlacesScreen, not the
// wizard — treat them differently: never replace their notes with a
// Wikipedia summary, and don't look up hours for the unscheduled ones.
export const guideSourcedPlaces = new WeakSet();

// Same place if the names match once accents, case and punctuation are gone.
export const placeNameKey = (s) =>
  String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "");

// "Cancun" finds "Cancún"; used to match a guide to a destination.
export const cityKey = (city, country) => `${placeNameKey(city)}|${placeNameKey(country)}`;

// A guide's visitable places (raw JSON), most important first:
//   1. Must See;
//   2. places in the guide's SHORTEST schedules — what made it into the
//      1-day plan is what the author considered essential;
//   3. places in its longer schedules;
//   4. unscheduled places ("More to see" and the like).
// Within a schedule, the order the day visits them. Paths, areas and hidden
// places are left out. A guide with no schedules falls back to Must See, then
// its own order.
export function rankGuidePlaces(guideJson) {
  const all = (guideJson?.places?.placesList || []).filter((p) => !p.Hidden && (!p.PType || p.PType === "Icon"));
  const events = guideJson?.schedule?.events || [];
  const schedules = new Map();
  events.forEach((e) => {
    if (!e.Start) return;
    const name = e.Itinerary || "Main";
    if (!schedules.has(name)) schedules.set(name, { name, days: new Set(), events: [] });
    const s = schedules.get(name);
    s.days.add(String(e.Start).slice(0, 10));
    s.events.push(e);
  });
  // Shortest first; "Main" last among equals, since it is often a copy of
  // one of the named schedules.
  const ordered = [...schedules.values()].sort(
    (a, b) => a.days.size - b.days.size || (a.name === "Main") - (b.name === "Main") || a.events.length - b.events.length
  );
  const rank = new Map(); // place id -> [schedule position, position in that schedule]
  ordered.forEach((s, si) => {
    s.events
      .slice()
      .sort((a, b) => (a.Start < b.Start ? -1 : a.Start > b.Start ? 1 : 0))
      .forEach((e, ei) => {
        String(e.Places || "").split(",").map((id) => id.trim()).filter(Boolean).forEach((id) => {
          if (!rank.has(id)) rank.set(id, [si, ei]);
        });
      });
  });
  const key = (p, i) => {
    const r = rank.get(String(p.ID));
    return [p.MustSee === true ? 0 : 1, r ? r[0] : Number.MAX_SAFE_INTEGER, r ? r[1] : i, i];
  };
  const seen = new Set();
  return all
    .map((p, i) => ({ p, k: key(p, i) }))
    .sort((a, b) => a.k[0] - b.k[0] || a.k[1] - b.k[1] || a.k[2] - b.k[2] || a.k[3] - b.k[3])
    .map((x) => x.p)
    .filter((p) => {
      const k = placeNameKey(p.Name);
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
}

// How long the guide's own author gives each place: the median length of the
// events that visit it (single-place events only), in minutes, by place id.
// Guide places carry no Duration of their own, and without one every place
// defaulted to a flat hour — a guide-built day came out with three times as
// many stops as a Google-built one, whose places arrive with real estimates.
// Found 2026-10-13.
export function durationsFromSchedules(guideJson) {
  const byPlace = new Map();
  (guideJson?.schedule?.events || []).forEach((e) => {
    const ids = String(e.Places || "").split(",").map((id) => id.trim()).filter(Boolean);
    if (ids.length !== 1 || !e.Start || !e.End) return;
    const minutes = Math.round((Date.parse(e.End) - Date.parse(e.Start)) / 60000);
    if (!Number.isFinite(minutes) || minutes < 10 || minutes > 600) return;
    if (!byPlace.has(ids[0])) byPlace.set(ids[0], []);
    byPlace.get(ids[0]).push(minutes);
  });
  const out = new Map();
  byPlace.forEach((list, id) => {
    const sorted = list.slice().sort((a, b) => a - b);
    out.set(id, sorted[Math.floor((sorted.length - 1) / 2)]);
  });
  return out;
}

// May `uid` take places from this guide? Their own guide, or one shared with
// Copy or Modify access (by name or for all users). Read-only isn't enough.
export function canCopyFrom(guideInfo, uid) {
  if (!guideInfo) return false;
  if (uid && uid !== "guest" && guideInfo.user_id === uid) return true;
  const level = Guide.myAccessLevel(guideInfo.access, uid);
  return level === "Copy" || level === "Modify";
}

// Ranked places of one loaded guide as real Place objects ready to be added
// to `targetGuide`: categories re-pointed to the target guide's own ids BY
// NAME (ids are per guide), groups and per-trip state dropped. IDs are left
// alone — Places.addPlace gives every added place a fresh one.
export function placesForTarget(guideJson, targetGuide) {
  const ranked = rankGuidePlaces(guideJson);
  const model = new Places();
  model.decodePlaces(null, null, null, null, { places: { placesList: ranked } });
  const curated = durationsFromSchedules(guideJson);
  const sourceCats = new Map((guideJson.categories || []).map((c) => [String(c.id), String(c.name || "").toLowerCase()]));
  const targetCats = new Map((targetGuide?.categories || []).map((c) => [String(c.name || "").toLowerCase(), String(c.id)]));
  model.placesList.forEach((p, i) => {
    // Visit length, before the categories are re-pointed: the place's own
    // Duration if the guide recorded one, else the author's own event length
    // for it, else the same type-based estimate Google places get (category
    // names stand in for Google's type keys: "Museum" → "museum"). Famous
    // sites get the same minimum either way.
    const raw = ranked[i];
    // A Duration stored on the place is the place's own and is always
    // respected, whatever the number. (Guides no longer get a filler written
    // into them on save — see Place.storageReplacer.)
    const own = raw.DurationUnset !== true && Number(raw.Duration) > 0 ? Number(raw.Duration) : null;
    const fromSchedule = curated.get(String(raw.ID));
    if (own != null) p.Duration = own;
    else if (fromSchedule != null) p.Duration = Common.applyMegaAttractionFloor(fromSchedule, p.Name);
    else {
      const typeKeys = (Array.isArray(raw.Category) ? raw.Category : []).map((id) => (sourceCats.get(String(id)) || "").replace(/[^a-z0-9]+/g, "_")).filter(Boolean);
      p.Duration = Common.estimateDurationForTypes(typeKeys, p.Name);
    }
    delete p.DurationUnset; // it has a real length now
    const cats = (Array.isArray(p.Category) ? p.Category : [])
      .map((id) => targetCats.get(sourceCats.get(String(id))))
      .filter((id) => id != null);
    p.Category = [...new Set(cats)];
    // A fresh ID when it clashes with a place already in the target guide —
    // typically the guide being built from is the one this guide was copied
    // from (a Featured guide), so the IDs are the same, and a lookup by ID
    // found the target guide's own place instead of this one (Build My
    // Trip's review showed it as an existing place, not a found one, and
    // adding it pointed at the wrong place). 2026-10-17.
    if (targetGuide?.places?.getPlace && targetGuide.places.getPlace(String(p.ID)) != null) {
      let id;
      do { id = String(Math.floor(Math.random() * 1000000000 + 1)); } while (targetGuide.places.getPlace(id) != null || model.getPlace(id) != null);
      p.ID = id;
    }
    // Shown as a found place, not one of the guide's own, until it's added
    // (addPlace sets USER): green name, "Only" search-results filter, active
    // filters bypassed — the same as a Featured or Google search result.
    // 2026-10-17.
    p.Source = Place_Source.FEATURED;
    p.Group = [];
    p.Favorite = false;
    p.Visited = false;
    p.scheduled = [];
    p.scheduledDates = [];
    p.inSchedule = false;
  });
  return model.placesList;
}

// Loads every guide in `sources` ([{ guide_info_id, guide_name }]) and
// returns their places pooled in pick order, duplicates (same name) removed.
// `readGuide(guide_info_id)` resolves to DataBase.readGuideTransaction's
// result. Guides that fail to load or don't allow copying are reported in
// `skipped`, not thrown — one bad guide shouldn't sink the whole trip.
export async function loadGuidePlaces(sources, uid, targetGuide, readGuide) {
  const places = [];
  const skipped = [];
  const seen = new Set();
  for (const s of sources || []) {
    let res = null;
    try { res = await readGuide(s.guide_info_id); } catch (e) { res = null; }
    if (!res || res.pass !== true || !res.res) { skipped.push({ ...s, why: "could not be opened" }); continue; }
    if (!canCopyFrom(res.infoDoc, uid)) { skipped.push({ ...s, why: "doesn't allow copying" }); continue; }
    let json = null;
    try { json = JSON.parse(res.res.data); } catch (e) { json = null; }
    if (!json) { skipped.push({ ...s, why: "could not be read" }); continue; }
    placesForTarget(json, targetGuide).forEach((p) => {
      const k = placeNameKey(p.Name);
      if (seen.has(k)) return;
      seen.add(k);
      guideSourcedPlaces.add(p);
      places.push(p);
    });
  }
  return { places, skipped };
}
